import { type FocusEvent, type ReactNode } from "react"

import { AnimatedElement } from "../AnimatedElement"
import { Button } from "../Button"
import { ButtonGroup } from "../ButtonGroup"
import { HairlineButton } from "../HairlineButton"
import { Icon } from "../Icon"
import { Text } from "../Text"
import { useGroup } from "./context"
import styles from "./GroupItem.module.css"

interface Props {
  index: number
  title?: string
  children?: ReactNode
  onMove?: (args: { index: number; offset: number }) => void
  onDelete?: (index: number) => void
}

export function GroupItem(props: Props) {
  const { index, title, onMove, onDelete, children } = props

  const { itemCount, draggable, activeItem, onActiveItemChange, onAddItem } =
    useGroup()

  const canMoveItem = itemCount > 1

  const showAddButton =
    activeItem !== undefined &&
    (index === activeItem || index === activeItem + 1)

  function setActive() {
    onActiveItemChange(index, true)
  }

  function setInactive() {
    onActiveItemChange(index, false)
  }

  function onBlur(event: FocusEvent) {
    // Just cycling through internal focusable elements, ignore.
    if (event.currentTarget.contains(event.relatedTarget)) return

    setInactive()
  }

  return (
    <article
      className={styles.groupItem}
      onMouseEnter={setActive}
      onFocus={setActive}
      onMouseLeave={setInactive}
      onBlur={onBlur}
    >
      <header className={styles.header} draggable={draggable && canMoveItem}>
        <Icon className={styles.headerIcon} name="viewDay" />
        <Text color="grey11" variant="small" component="span">
          {title}
        </Text>

        <ButtonGroup sx={{ marginLeft: "auto" }} size="small" color="grey">
          {canMoveItem && (
            <ButtonGroup density="compact">
              <Button
                disabled={index === 0}
                startIcon="arrowUpward"
                onClick={() => onMove?.({ index, offset: -1 })}
              />
              <Button
                disabled={index === itemCount - 1}
                startIcon="arrowDownward"
                onClick={() => onMove?.({ index, offset: 1 })}
              />
            </ButtonGroup>
          )}
          <Button startIcon="delete" onClick={() => onDelete?.(index)} />

          {canMoveItem && (
            <Icon
              name="dragIndicator"
              color="grey8"
              size="small"
              sx={{ alignSelf: "center" }}
            />
          )}
        </ButtonGroup>

        <div className={styles.addButton}>
          <AnimatedElement enterDuration={150} exitDuration={100}>
            {showAddButton && (
              <HairlineButton icon="add" onClick={() => onAddItem?.(index)} />
            )}
          </AnimatedElement>
        </div>
      </header>

      <section className={styles.content}>{children}</section>
    </article>
  )
}
